import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { DesignationField } from 'components/WorkspaceEditor/features/drawingDesignation'
import { parseDesignationText } from 'components/WorkspaceEditor/features/drawingDesignation/services/textParser'
import React, { memo, useMemo } from 'react'
import { ContentEditorProps } from '../../ContentEditor'
import { elementToEditorData } from '../../ContentEditor/contentUtils'
import { withLazyLoad } from '../../withLazyComponent'
import { KeyedText } from './KeyedText'

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/** Props for designation field (for Product Type and Article nodes) */
type DesignationProps = {
    /** Whether this is a Product Type node (for drawing designation field) */
    isProductType?: boolean
    /** Whether this is an Article node (for drawing designation field) */
    isArticle?: boolean
    /** Content item ID for drawing designation */
    contentItemId?: string
    /** Node ID for drawing designation */
    nodeId?: string
    /** Whether this key is a PT key (from template) */
    isPTKey?: boolean
    /** Raw drawing designation from the server (avoids lossy text parsing) */
    serverDesignation?: string
    /** Indicates if PT is created from a Designation Field via Import. Used to hide/display default Designation Field */
    pTDesignationKey?: string
}

/** Props for the text editor component */
interface EditorContentProps extends ContentEditorProps {
    /** The label text that appears before the editable portion */
    keyedText: string
    /** The editable text portion after the label */
    keyedTextValue?: string
    /** CSS styling classes */
    classes?: string
    /** Designation field props (for Product Type nodes) */
    designationProps?: DesignationProps
}

/**
 * A text editor that handles two display formats:
 * 1. Regular text editing (standard mode)
 * 2. Label-value pairs where the label (with semicolon) is fixed and only the value is editable
 *
 * Content team members always see the standard editor regardless of format.
 *
 * @param {EditorContentProps} props - Props for the component.
 */
export const EditorContent = memo<EditorContentProps>(
    ({
        initialData,
        initialSuggestions,
        clickCoordinates,
        handlers,
        keyedText,
        keyedTextValue,
        classes,
        designationProps,
    }) => {
        const isContentTeamFromStudioPage = useShallowSelector(
            (state) => state.isContentTeamFromStudioPage
        )
        const { isProductType, isArticle, contentItemId, nodeId, isPTKey, serverDesignation, pTDesignationKey } = designationProps || {}

        // Check if this is a designation-enabled node (Product Type or Article)
        const isDesignationNode = isProductType || isArticle

        // For Product Types and Articles, parse keyedText using server designation for accuracy
        const textPrefix = useMemo(() => {
            if (isDesignationNode) {
                return parseDesignationText(keyedText, { serverDesignation }).prefix
            }
            return keyedText
        }, [keyedText, isDesignationNode, serverDesignation])

        // Whether there's a real colon-delimited key/value split for this PT item — true
        // both for normal locked keys (isKeyedText) and for OM/content-team excluded
        // content that still has a real colon (see useKeyedText's hasKeySplit). Content
        // excluded for OTHER reasons (user-added text, MFR/BOD headers, post-key-delimiter
        // elements) never gets keyedTextValue populated, so this stays false for them.
        const hasRealKeySplit = keyedTextValue !== undefined

        // Extract only the value portion for editing, so the key text itself never
        // becomes editable CKEditor content. Covers both locked keys (isKeyedText,
        // non-content-team) and unlocked PT keys for content-team/OM users — this branch
        // has no !isContentTeamFromStudioPage guard because the key is stripped for them
        // too (their real colon split still needs the key excluded from editable content);
        // usePlainTextController's shouldPrependKeyOnSave re-adds it for both cases on save.
        const editorInitialData = useMemo(() => {
            let result
            if (isPTKey && keyedTextValue !== undefined) {
                // Check if keyedTextValue is empty/whitespace - if so, use empty paragraph
                const trimmedValue = keyedTextValue.trim()
                if (!trimmedValue) {
                    result = '<p></p>'
                } else {
                    // Create a proper element object with id and type for elementToEditorData
                    result = elementToEditorData({
                        id: contentItemId || 'temp-id',
                        text: keyedTextValue,
                        type: 'Text'
                    })
                }
            } else {
                // For all other cases (unlocked keyed text, regular text), use full initialData
                result = initialData
            }

            return result
        }, [isPTKey, keyedTextValue, initialData, contentItemId])

        const editorComponent = (
            <ContentEditor
                initialData={editorInitialData}
                initialSuggestions={initialSuggestions}
                clickCoordinates={clickCoordinates}
                handlers={{
                    onReady: handlers?.onReady,
                    onBlur: handlers?.onBlur,
                    onChange: handlers?.onChange,
                    onConfigComplete: handlers?.onConfigComplete,
                }}
            />
        )

        // Check if this is a Product Type or Article with designation format
        // Only render designation for PT keys (first element) or Article first items to avoid duplication
        const hasDesignationMode =
            isDesignationNode && contentItemId && nodeId && (isPTKey || isArticle)

        // Content-team/OM users editing an excluded key's value (not a genuinely locked
        // key) never saw the drawing-designation input before — only show it for real
        // locked-key editing, not for their bypassed key/value split.
        const displayEditableDesignationField =
            (isPTKey || isArticle) &&
            isDesignationNode &&
            contentItemId &&
            nodeId &&
            !isContentTeamFromStudioPage

        const displayDesignationFieldBasedOnDesignationKey = (pTDesignationKey === undefined || pTDesignationKey === "NONE" || pTDesignationKey === "INSERTDRAWINGDESIGNATION" )

        // show standard editor, for regular text, or content-team/OM content with no real
        // key/value split (nothing to lock in that case). The `|| isContentTeamFromStudioPage`
        // is what excludes content-team users from the designation-only render branch below
        // (line ~153) when hasDesignationMode is true — without it they'd fall through to
        // that branch instead of getting the standard editor.
        if (
            (!hasRealKeySplit && (!hasDesignationMode || isContentTeamFromStudioPage)) ||
            !isPTKey
        ) {
            return editorComponent
        }

        if (!hasRealKeySplit && hasDesignationMode) {
            return (
                <>
                    <KeyedText
                        keyedText={textPrefix}
                        classes={classes}
                        data-testid="keyed-text-key"
                    />
                    <DesignationField
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        contentItemId={contentItemId}
                        editable={true}
                        inactive={false}
                        showPrefix={false}
                        showSuffix={false}
                        serverDesignation={serverDesignation}
                    />
                </>
            )
        }

        // show label separately from editable value, for keyed text
        return (
            <>
                <KeyedText
                    keyedText={isPTKey ? textPrefix : keyedText}
                    classes={classes}
                    data-testid="keyed-text-key"
                    isPTKey={isPTKey}
                />
                {displayEditableDesignationField ? (
                    displayDesignationFieldBasedOnDesignationKey ?
                        <>
                            <DesignationField
                                keyedText={keyedText}
                                keyedTextValue={keyedTextValue}
                                contentItemId={contentItemId}
                                editable={true}
                                inactive={false}
                                showPrefix={false}
                                showSuffix={false}
                                isArticle={isArticle}
                                serverDesignation={serverDesignation}
                            />
                            {!isArticle && ':'}
                        </>
                    : <><span>:</span></>
                ) : (
                    !isArticle && ':'
                )}
                {editorComponent}
            </>
        )
    }
)
